/**
 * Shared colour palette for the Site Diary module.
 *
 * Keys mirror `DIARY_COLOURS` in `backend/models/schemas.py` so a project's
 * stored colour string maps to the same visual on the client. Each entry
 * bundles the Tailwind class names the diary UI needs plus the raw hex
 * (used for inline styles where Tailwind's dynamic class name inference
 * can't help — e.g. `borderLeftColor`).
 *
 * If you extend this palette also extend DIARY_COLOURS on the backend.
 */
export const DIARY_COLOURS = {
  blue:    { bg: 'bg-blue-500/15',    border: 'border-blue-500/50',    text: 'text-blue-300',    dot: 'bg-blue-500',    hex: '#3B82F6' },
  emerald: { bg: 'bg-emerald-500/15', border: 'border-emerald-500/50', text: 'text-emerald-300', dot: 'bg-emerald-500', hex: '#10B981' },
  purple:  { bg: 'bg-purple-500/15',  border: 'border-purple-500/50',  text: 'text-purple-300',  dot: 'bg-purple-500',  hex: '#8B5CF6' },
  amber:   { bg: 'bg-amber-500/15',   border: 'border-amber-500/50',   text: 'text-amber-300',   dot: 'bg-amber-500',   hex: '#F59E0B' },
  pink:    { bg: 'bg-pink-500/15',    border: 'border-pink-500/50',    text: 'text-pink-300',    dot: 'bg-pink-500',    hex: '#EC4899' },
  cyan:    { bg: 'bg-cyan-500/15',    border: 'border-cyan-500/50',    text: 'text-cyan-300',    dot: 'bg-cyan-500',    hex: '#06B6D4' },
  red:     { bg: 'bg-red-500/15',     border: 'border-red-500/50',     text: 'text-red-300',     dot: 'bg-red-500',     hex: '#EF4444' },
  indigo:  { bg: 'bg-indigo-500/15',  border: 'border-indigo-500/50',  text: 'text-indigo-300',  dot: 'bg-indigo-500',  hex: '#6366F1' },
  teal:    { bg: 'bg-teal-500/15',    border: 'border-teal-500/50',    text: 'text-teal-300',    dot: 'bg-teal-500',    hex: '#14B8A6' },
  orange:  { bg: 'bg-orange-500/15',  border: 'border-orange-500/50',  text: 'text-orange-300',  dot: 'bg-orange-500',  hex: '#F97316' },
  lime:    { bg: 'bg-lime-500/15',    border: 'border-lime-500/50',    text: 'text-lime-300',    dot: 'bg-lime-500',    hex: '#84CC16' },
  fuchsia: { bg: 'bg-fuchsia-500/15', border: 'border-fuchsia-500/50', text: 'text-fuchsia-300', dot: 'bg-fuchsia-500', hex: '#D946EF' },
};

/**
 * Entry type palette. Each type gets its own distinct colour so the
 * timeline reads at a glance. Kept separate from project colours because
 * the palettes serve different visual roles.
 */
export const ENTRY_TYPE_META = {
  note:        { label: 'Note',        icon: 'FileText',    dot: 'bg-slate-400',   bg: 'bg-slate-500/10',   border: 'border-slate-500/40'   },
  labour:      { label: 'Labour',      icon: 'Users',       dot: 'bg-blue-500',    bg: 'bg-blue-500/10',    border: 'border-blue-500/40'    },
  delivery:    { label: 'Delivery',    icon: 'Truck',       dot: 'bg-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/40' },
  weather:     { label: 'Weather',     icon: 'CloudRain',   dot: 'bg-cyan-500',    bg: 'bg-cyan-500/10',    border: 'border-cyan-500/40'    },
  delay:       { label: 'Delay',       icon: 'AlertOctagon', dot: 'bg-red-500',    bg: 'bg-red-500/10',     border: 'border-red-500/40'     },
  rfi:         { label: 'RFI',         icon: 'HelpCircle',  dot: 'bg-amber-500',   bg: 'bg-amber-500/10',   border: 'border-amber-500/40'   },
  instruction: { label: 'Instruction', icon: 'ClipboardCheck', dot: 'bg-purple-500', bg: 'bg-purple-500/10', border: 'border-purple-500/40' },
  visit:       { label: 'Site Visit',  icon: 'UserCheck',   dot: 'bg-indigo-500',  bg: 'bg-indigo-500/10',  border: 'border-indigo-500/40'  },
};

/**
 * Utility — returns the colour bundle for a given project record. Falls
 * back to blue if the project was created before the colour field existed
 * (should not happen post-migration but defensive).
 */
export const projectColour = (project) => DIARY_COLOURS[project?.colour] || DIARY_COLOURS.blue;
